<template>
    <div class="main">
        <c-panel title="EXPORTAÇÃO SEB / INEP" titleIcon="fas fa-file-export">

            <!--
                O aviso é permanente e não fechável de propósito.

                A parte oficial deste leiaute é a estrutura dos 13 campos, transcrita do manual.
                Codificação, fim de linha, cabeçalho e empacotamento NÃO estão no manual e são
                decisão nossa. Escrever "layout oficial completo" sem essa distinção seria
                afirmar mais do que temos como provar.
            -->
            <el-alert type="info" :closable="false" class="u-mb-15" show-icon
                v-if="layout"
                :title="`${layout.rotulo} — ${layout.campos.length} campos`">
                <div><small><b>Fonte:</b> {{ layout.fonte }}</small></div>
                <div><small>{{ layout.aviso }}</small></div>
            </el-alert>

            <el-form label-position="top" @submit.native.prevent>
                <el-row :gutter="10" class="u-mb-15">
                    <el-col :xs="24" :sm="5">
                        <el-form-item label="Ano letivo" class="u-mb-0">
                            <el-select v-model="periodoId" style="width: 100%;" @change="limpar">
                                <el-option v-for="p in periodos" :key="p.id" :value="p.id" :label="p.nome"></el-option>
                            </el-select>
                        </el-form-item>
                    </el-col>
                    <el-col :xs="24" :sm="9">
                        <el-form-item label="Instituição" class="u-mb-0">
                            <el-select v-model="instituicaoId" clearable filterable style="width: 100%;" @change="limpar">
                                <el-option v-for="i in instituicoesNoEscopo" :key="i.id" :value="i.id" :label="i.nome"></el-option>
                            </el-select>
                        </el-form-item>
                    </el-col>
                    <el-col :xs="24" :sm="10" class="u-text-right u-mt-25">
                        <el-button type="primary" size="small" icon="el-icon-search"
                            :loading="validando" :disabled="!periodoId" @click="validar">VALIDAR DADOS</el-button>

                        <el-button class="u-ml-10" type="success" size="small" icon="el-icon-download"
                            :loading="gerando" :disabled="!podeGerar" @click="gerar">GERAR ARQUIVO</el-button>
                    </el-col>
                </el-row>
            </el-form>

            <el-tabs v-model="aba">

                <!-- ==================== VALIDAÇÃO ==================== -->
                <el-tab-pane name="validacao">
                    <span slot="label">
                        VALIDAÇÃO
                        <el-badge v-if="resultado && resultado.erros" :value="resultado.erros" type="danger" class="u-ml-5"></el-badge>
                    </span>

                    <el-alert v-if="!resultado" type="info" :closable="false" show-icon
                        title="Selecione o ano letivo e clique em VALIDAR DADOS."
                        description="A validação apenas confere a base. Nenhum arquivo é gerado neste passo."></el-alert>

                    <div v-if="resultado">
                        <el-row :gutter="10" class="u-mb-20">
                            <el-col :xs="12" :sm="4"><div class="kpi"><span class="n">{{ resultado.instituicoes }}</span><span class="r">Instituições</span></div></el-col>
                            <el-col :xs="12" :sm="4"><div class="kpi"><span class="n">{{ resultado.alunos }}</span><span class="r">Matrículas</span></div></el-col>
                            <el-col :xs="12" :sm="4"><div class="kpi"><span class="n">{{ resultado.validos }}</span><span class="r">Registros válidos</span></div></el-col>
                            <el-col :xs="12" :sm="4"><div class="kpi" :class="{ 'kpi-erro': resultado.erros > 0 }"><span class="n">{{ resultado.erros }}</span><span class="r">Erros</span></div></el-col>
                            <el-col :xs="12" :sm="4"><div class="kpi"><span class="n">{{ resultado.avisos }}</span><span class="r">Avisos</span></div></el-col>
                        </el-row>

                        <el-alert v-if="resultado.pode_gerar" type="success" :closable="false" show-icon class="u-mb-15"
                            title="0 erros. O arquivo pode ser gerado."></el-alert>

                        <el-alert v-else type="error" :closable="false" show-icon class="u-mb-15"
                            title="Existem erros bloqueantes. Corrija os cadastros e valide novamente."
                            description="O arquivo só é gerado com zero erros — um registro inválido faz o SEB recusar o lote."></el-alert>

                        <el-alert v-if="resultado.cursos_sem_mapa && resultado.cursos_sem_mapa.length"
                            type="warning" :closable="false" show-icon class="u-mb-15"
                            :title="`${resultado.cursos_sem_mapa.length} curso(s) sem correspondência de etapa/série`"
                            description="Configure na aba ETAPA/SÉRIE. O sistema não adivinha o código do INEP a partir do ID interno."></el-alert>

                        <div v-if="resultado.problemas.length" class="u-mb-10 u-text-right">
                            <el-button size="mini" icon="el-icon-document"
                                @click="baixarInconsistencias">BAIXAR RELATÓRIO DE INCONSISTÊNCIAS (CSV)</el-button>
                        </div>

                        <el-table v-if="resultado.problemas.length" :data="problemasPagina" size="mini" border stripe>
                            <el-table-column prop="instituicao" label="Instituição" min-width="150"></el-table-column>
                            <el-table-column prop="aluno" label="Aluno" min-width="150"></el-table-column>
                            <el-table-column prop="matricula" label="Matrícula" width="120"></el-table-column>
                            <el-table-column prop="campo" label="Campo" width="150"></el-table-column>
                            <el-table-column prop="valor" label="Valor" width="130"></el-table-column>
                            <el-table-column prop="erro" label="Problema" min-width="250"></el-table-column>
                        </el-table>

                        <el-pagination v-if="resultado.problemas.length > porPagina" class="u-mt-10"
                            layout="total, prev, pager, next" :page-size="porPagina"
                            :total="resultado.problemas.length" :current-page.sync="pagina"></el-pagination>
                    </div>
                </el-tab-pane>

                <!-- ==================== ETAPA/SÉRIE (de/para) ==================== -->
                <el-tab-pane name="mapa">
                    <span slot="label">
                        ETAPA/SÉRIE
                        <el-badge v-if="mapa.pendentes.length" :value="mapa.pendentes.length" type="warning" class="u-ml-5"></el-badge>
                    </span>

                    <el-alert type="info" :closable="false" show-icon class="u-mb-15"
                        title="De/para entre os cursos da rede e os códigos de etapa/série do SEB"
                        description="O ID interno do curso NÃO é o código do INEP. A sugestão vem preenchida a partir do nome do curso, mas só passa a valer depois de salva."></el-alert>

                    <el-table :data="mapa.pendentes" size="mini" border stripe v-if="mapa.pendentes.length">
                        <el-table-column prop="curso" label="Curso da rede" min-width="220"></el-table-column>
                        <el-table-column label="Código SEB (NU_ANO_SERIE)" min-width="330">
                            <template slot-scope="s">
                                <el-select v-model="s.row.sugestao" size="mini" filterable style="width: 100%;"
                                    placeholder="Selecione a etapa/série correspondente">
                                    <el-option v-for="o in mapa.opcoes" :key="o.codigo" :value="o.codigo"
                                        :label="`${o.codigo} — ${o.descricao}`"></el-option>
                                </el-select>
                            </template>
                        </el-table-column>
                        <el-table-column label="" width="100" align="center">
                            <template slot-scope="s">
                                <el-button size="mini" type="primary" :disabled="!s.row.sugestao"
                                    @click="salvarMapa(s.row)">SALVAR</el-button>
                            </template>
                        </el-table-column>
                    </el-table>

                    <!--
                        Antes da primeira consulta não se afirma nada.

                        A versão anterior mostrava "todos os cursos têm correspondência" com a
                        lista simplesmente ainda vazia — dizia o oposto da verdade justo quando
                        nenhum de/para existia.
                    -->
                    <el-alert v-else-if="!mapaCarregado" type="info" :closable="false" show-icon
                        title="Selecione o ano letivo e clique em VALIDAR DADOS para levantar os cursos que precisam de correspondência."></el-alert>

                    <el-alert v-else type="success" :closable="false" show-icon
                        title="Todos os cursos do recorte selecionado têm correspondência configurada."></el-alert>

                    <div v-if="mapa.configurados.length" class="u-mt-20">
                        <h4>Correspondências configuradas</h4>
                        <el-table :data="mapa.configurados" size="mini" border stripe>
                            <el-table-column prop="curso" label="Curso da rede" min-width="220"></el-table-column>
                            <el-table-column label="Código SEB" width="380">
                                <template slot-scope="s">{{ s.row.nu_ano_serie }} — {{ descricaoSerie(s.row.nu_ano_serie) }}</template>
                            </el-table-column>
                        </el-table>
                    </div>
                </el-tab-pane>

                <!-- ==================== PRÉVIA ==================== -->
                <el-tab-pane label="PRÉVIA DO ARQUIVO" name="previa">
                    <el-alert type="warning" :closable="false" show-icon class="u-mb-15"
                        title="CPF e nome social aparecem mascarados nesta prévia"
                        description="A prévia serve para conferir a estrutura das colunas. O arquivo gerado leva os valores completos."></el-alert>

                    <div v-if="resultado && resultado.previa">
                        <pre class="previa">{{ resultado.previa.cabecalho }}
{{ resultado.previa.linhas.join('\n') }}</pre>
                    </div>
                    <el-alert v-else type="info" :closable="false" title="Valide os dados para ver a prévia."></el-alert>
                </el-tab-pane>

                <!-- ==================== LAYOUT ==================== -->
                <el-tab-pane label="LAYOUT" name="layout">
                    <div v-if="layout">
                        <h4>Estrutura oficial — transcrita do manual</h4>
                        <el-table :data="layout.campos" size="mini" border stripe>
                            <el-table-column label="#" width="50" type="index"></el-table-column>
                            <el-table-column prop="nome" label="Campo" width="200"></el-table-column>
                            <el-table-column prop="tipo" label="Tipo" width="90"></el-table-column>
                            <el-table-column prop="tamanho" label="Tam." width="70"></el-table-column>
                            <el-table-column label="Obrigatório" width="110" align="center">
                                <template slot-scope="s">
                                    <el-tag size="mini" :type="s.row.obrigatorio ? 'danger' : 'info'">
                                        {{ s.row.obrigatorio ? 'Sim' : 'Não' }}
                                    </el-tag>
                                </template>
                            </el-table-column>
                            <el-table-column prop="descricao" label="Descrição" min-width="250"></el-table-column>
                        </el-table>

                        <h4 class="u-mt-20">Convenções adotadas por esta implementação</h4>
                        <el-alert type="warning" :closable="false" show-icon class="u-mb-10"
                            title="Os itens abaixo NÃO são especificados pelo manual do SEB consultado."
                            description="São decisões técnicas necessárias para serializar o arquivo. Se o INEP publicar exigência diferente, é aqui que se ajusta."></el-alert>

                        <el-table :data="convencoes" size="mini" border stripe>
                            <el-table-column prop="aspecto" label="Aspecto" width="180"></el-table-column>
                            <el-table-column prop="valor" label="Adotado" width="230"></el-table-column>
                            <el-table-column prop="observacao" label="Por que é convenção" min-width="300"></el-table-column>
                        </el-table>
                    </div>
                </el-tab-pane>

                <!-- ==================== HISTÓRICO ==================== -->
                <el-tab-pane label="HISTÓRICO" name="historico">
                    <el-table :data="historico" size="mini" border stripe>
                        <el-table-column prop="ano" label="Ano" width="70"></el-table-column>
                        <el-table-column label="Escopo" min-width="150">
                            <template slot-scope="s">{{ s.row.instituicao_nome || 'Toda a rede acessível' }}</template>
                        </el-table-column>
                        <el-table-column prop="versao_layout" label="Layout" min-width="150"></el-table-column>
                        <el-table-column prop="usuario_nome" label="Gerado por" min-width="120"></el-table-column>
                        <el-table-column label="Data/hora" width="140">
                            <template slot-scope="s">{{ dataHora(s.row.created_at) }}</template>
                        </el-table-column>
                        <el-table-column prop="quantidade_registros" label="Registros" width="90" align="right"></el-table-column>
                        <el-table-column label="SHA-256" min-width="220">
                            <template slot-scope="s"><code class="hash">{{ s.row.hash_arquivo }}</code></template>
                        </el-table-column>
                        <el-table-column label="" width="100" align="center">
                            <template slot-scope="s">
                                <el-button size="mini" icon="el-icon-download" @click="baixar(s.row)">BAIXAR</el-button>
                            </template>
                        </el-table-column>
                    </el-table>
                </el-tab-pane>
            </el-tabs>

            <!-- Resultado da geração: é a prova de qual arquivo saiu. -->
            <el-dialog title="Arquivo gerado" :visible.sync="dialogGerado" width="560px">
                <div v-if="gerado">
                    <p><b>Arquivo:</b><br><code>{{ gerado.arquivo }}</code></p>
                    <p><b>Registros:</b> {{ gerado.registros }}</p>
                    <p><b>Gerado em:</b> {{ gerado.gerado_em }}</p>
                    <p><b>Layout:</b> {{ gerado.layout }}</p>
                    <p><b>SHA-256:</b><br><code class="hash">{{ gerado.hash }}</code></p>
                </div>
                <span slot="footer">
                    <el-button size="small" @click="dialogGerado = false">FECHAR</el-button>
                    <el-button class="u-ml-10" size="small" type="primary" icon="el-icon-download"
                        @click="baixar(gerado)">BAIXAR ZIP</el-button>
                </span>
            </el-dialog>

        </c-panel>
    </div>
</template>

<script>
/**
 * FASE 6 — Exportação SEB / INEP (EDUCAÇÃO item 3 do edital).
 *
 * Fluxo: escolher ano → VALIDAR → configurar etapa/série → corrigir cadastros →
 * VALIDAR de novo → GERAR → baixar o ZIP.
 *
 * A tela GERA arquivo. Ela não transmite nada ao INEP: o envio continua sendo feito pelo
 * servidor no portal do órgão, e prometer transmissão automática aqui seria falso.
 */
import { periodosLetivosMixin } from '@/shared/periodos-letivos'
export default {
    mixins: [periodosLetivosMixin],
    data: () => ({
        aba: 'validacao',
        instituicoes: [], periodoId: null, instituicaoId: null,
        layout: null, resultado: null, historico: [], gerado: null,
        mapa: { pendentes: [], configurados: [], opcoes: [] },
        validando: false, gerando: false, dialogGerado: false, mapaCarregado: false,
        pagina: 1, porPagina: 15
    }),

    computed: {
        podeGerar () { return !!(this.resultado && this.resultado.pode_gerar) && !this.gerando },

        problemasPagina () {
            if (!this.resultado) return []
            const i = (this.pagina - 1) * this.porPagina
            return this.resultado.problemas.slice(i, i + this.porPagina)
        },

        /**
         * Só as escolas que o usuário consegue exportar.
         *
         * `instituicao/getAll` devolve a rede inteira — é assim que as outras telas o usam.
         * Oferecer aqui uma escola fora do vínculo faria o secretário escolher, validar e
         * receber "0 matrículas", parecendo defeito de cadastro em vez de falta de acesso.
         */
        instituicoesNoEscopo () {
            const p = this.layout && this.layout.instituicoes_permitidas
            if (!p) return this.instituicoes   // null = perfil de rede, sem restrição
            return this.instituicoes.filter(i => p.map(Number).includes(Number(i.id)))
        },

        /** As convenções viram tabela para ficarem visíveis, não escondidas num rodapé. */
        convencoes () {
            return (this.layout && this.layout.convencoes) || []
        }
    },

    mounted () {
        this.periodoId = this.$store.state.config.periodo ? this.$store.state.config.periodo.id : null

        this.$axios.get(`${this.$store.state.config.baseUrlApi}/api/instituicao/getAll`,
            this.$store.getters.getHeaderAuth
        ).then(r => { this.instituicoes = Array.isArray(r.data) ? r.data : [] }).catch(() => {})

        this.carregarLayout()
        this.carregarHistorico()
        this.carregarMapa()
    },

    methods: {
        url (p) { return `${this.$store.state.config.baseUrlApi}/api/exportacao-seb/${p}` },

        params () {
            return { periodo_letivo_id: this.periodoId, instituicao_id: this.instituicaoId }
        },

        limpar () { this.resultado = null; this.pagina = 1; this.carregarMapa() },

        carregarLayout () {
            this.$axios.get(this.url('layout'), this.$store.getters.getHeaderAuth)
                .then(r => { this.layout = r.data }).catch(() => {})
        },

        carregarHistorico () {
            this.$axios.get(this.url('historico'), this.$store.getters.getHeaderAuth)
                .then(r => { this.historico = Array.isArray(r.data) ? r.data : [] }).catch(() => {})
        },

        carregarMapa () {
            if (!this.periodoId) return
            this.$axios.post(this.url('mapa-serie'), this.params(), this.$store.getters.getHeaderAuth)
                .then(r => { this.mapa = r.data; this.mapaCarregado = true }).catch(() => {})
        },

        validar () {
            this.validando = true
            this.pagina = 1

            this.$axios.post(this.url('validar'), this.params(), this.$store.getters.getHeaderAuth)
                .then(r => {
                    this.resultado = r.data
                    this.carregarMapa()

                    // Leva o operador direto para onde está o impedimento.
                    if (r.data.cursos_sem_mapa && r.data.cursos_sem_mapa.length) this.aba = 'mapa'
                })
                .catch(e => this.erro(e))
                .then(() => { this.validando = false })
        },

        salvarMapa (linha) {
            this.$axios.post(this.url('salvar-mapa-serie'),
                { curso_id: linha.curso_id, nu_ano_serie: linha.sugestao },
                this.$store.getters.getHeaderAuth
            ).then(() => {
                window.eventBus.$emit('alert', { msg: 'Correspondência salva.', type: 'success' })
                this.carregarMapa()
                this.limpar()   // o resultado anterior ficou desatualizado
            }).catch(e => this.erro(e))
        },

        gerar () {
            this.gerando = true

            this.$axios.post(this.url('gerar'), this.params(), this.$store.getters.getHeaderAuth)
                .then(r => {
                    this.gerado = r.data
                    this.dialogGerado = true
                    this.carregarHistorico()
                })
                .catch(e => this.erro(e))
                .then(() => { this.gerando = false })
        },

        /**
         * O download passa por `auth:api`, então não dá para usar um `<a href>` simples:
         * o token não viaja na URL. Busca como blob e entrega ao navegador.
         */
        baixar (e) {
            if (!e || !e.id) return

            const cfg = Object.assign({ responseType: 'blob' }, this.$store.getters.getHeaderAuth)

            this.$axios.get(this.url(`baixar/${e.id}`), cfg)
                .then(r => this.salvarBlob(r.data, e.arquivo || e.nome_arquivo, 'application/zip'))
                .catch(() => window.eventBus.$emit('alert',
                    { msg: 'Não foi possível baixar o arquivo.', type: 'error' }))
        },

        baixarInconsistencias () {
            const cfg = Object.assign({ responseType: 'blob' }, this.$store.getters.getHeaderAuth)

            this.$axios.post(this.url('inconsistencias'), this.params(), cfg)
                .then(r => this.salvarBlob(r.data,
                    `seb_inconsistencias_${this.periodoId}.csv`, 'text/csv'))
                .catch(() => window.eventBus.$emit('alert',
                    { msg: 'Não foi possível gerar o relatório.', type: 'error' }))
        },

        salvarBlob (dados, nome, mime) {
            const url = window.URL.createObjectURL(new Blob([dados], { type: mime }))
            const a = document.createElement('a')
            a.href = url
            a.download = nome
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            window.URL.revokeObjectURL(url)
        },

        descricaoSerie (codigo) {
            const o = (this.mapa.opcoes || []).find(x => x.codigo === codigo)
            return o ? o.descricao : ''
        },

        dataHora (v) {
            if (!v) return ''
            const d = new Date(v.replace(' ', 'T'))
            return isNaN(d) ? v : d.toLocaleString('pt-BR')
        },

        erro (e) {
            const d = e && e.response ? e.response.data : null
            window.eventBus.$emit('alert', {
                msg: (d && d.erro) ? d.erro : 'Não foi possível concluir a operação.',
                type: 'error'
            })
        }
    }
}
</script>

<style scoped>
.kpi { background: #f5f7fa; border: 1px solid #e4e7ed; border-radius: 4px; padding: 10px; text-align: center; }
.kpi .n { display: block; font-size: 22px; font-weight: 700; color: #303133; }
.kpi .r { display: block; font-size: 11px; color: #909399; text-transform: uppercase; }
.kpi-erro { background: #fef0f0; border-color: #fbc4c4; }
.kpi-erro .n { color: #f56c6c; }
.previa { background: #2d2d2d; color: #d4d4d4; padding: 12px; border-radius: 4px;
          font-size: 11px; overflow-x: auto; white-space: pre; }
.hash { font-size: 10px; word-break: break-all; }
.u-ml-5 { margin-left: 5px; }
</style>
